// ============================================================================
// SYUT EMPLOYEE SELECT
// ============================================================================
// Domain wrapper around SyutSelect pre-configured to search the Employee master.
// Accepts a `role` prop to filter to managers, partners, or all employees.
// Scales to any number of employees (server-side searchTerm).

import React, { useCallback } from 'react';
import { SyutSelect } from '../SyutSelect';
import { employeeService } from 'api/services/employeeService';
import { useResolvedSeedOptions } from './useResolvedSeedOptions';

export type EmployeeRole = 'manager' | 'partner' | 'any';

/** One place that builds an employee's label, so the dropdown and the resolved
 *  selected value can never disagree about how an employee is written. */
const employeeLabel = (e: any) => `${e.name || ''}${e.code ? ` (${e.code})` : ''}`.trim();

export interface SyutEmployeeSelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  /** 'manager' → isManager=true, 'partner' → isPartner=true, 'any' (default) → no role filter */
  role?: EmployeeRole;
  seedOptions?: { value: string; label: string }[];
  includeInactive?: boolean;
  includeLeft?: boolean;
  /** Rows per server query (default: 500). */
  pageSize?: number;
  disabled?: boolean;
}

export const SyutEmployeeSelect: React.FC<SyutEmployeeSelectProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  isClearable = true,
  placeholder,
  size = 'sm',
  role = 'any',
  seedOptions,
  includeInactive = false,
  includeLeft = false,
  pageSize = 500,
  disabled,
}) => {
  const defaultPlaceholder =
    placeholder ??
    (role === 'manager'
      ? 'Type 3 letters to search managers...'
      : role === 'partner'
      ? 'Type 3 letters to search partners...'
      : 'Type 3 letters to search employees...');

  // A selected employee outside the loaded page would otherwise render blank —
  // see useResolvedSeedOptions for the full account.
  //
  // This lookup is deliberately NOT role-filtered. A record can hold a manager
  // who has since lost the flag, or an employee who has left; the label still
  // has to render, otherwise the field blanks and the user "fixes" it by
  // silently reassigning the engagement. What the picker OFFERS stays filtered
  // by role — this only resolves what is already stored.
  const fetchOption = useCallback(async (id: string) => {
    const res = await employeeService.getById(id);
    const e: any = res?.data;
    return e ? { value: String(e.id), label: employeeLabel(e) } : null;
  }, []);

  const effectiveSeedOptions = useResolvedSeedOptions(value, seedOptions, fetchOption);

  return (
    <SyutSelect
      label={label}
      value={value}
      isAsync
      loadOptions={async (input: string) => {
        const res = await employeeService.getAll({
          page: 1,
          pageSize,
          searchTerm: input || undefined,
          isManager: role === 'manager' ? true : undefined,
          isPartner: role === 'partner' ? true : undefined,
          includeInactive,
          includeLeft,
        });
        return res.data.map((e: any) => ({ value: e.id, label: employeeLabel(e) }));
      }}
      defaultOptions
      cacheOptions={false}
      seedOptions={effectiveSeedOptions}
      debounceDelay={300}
      minSearchLength={3}
      isClearable={isClearable}
      size={size}
      placeholder={defaultPlaceholder}
      error={error}
      required={required}
      isDisabled={disabled}
      onChange={(val) => onChange((val as string) || '')}
    />
  );
};

export default SyutEmployeeSelect;
